import {
  CloseOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  SaveOutlined,
} from '@ant-design/icons';
import {
  Drawer,
  Button,
  message,
  Spin,
  Row,
  Col,
  Typography,
  ModalProps,
  Modal,
  notification,
} from 'antd';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import PageTitle from '../../components/PageTitle';
import StaffUserForm, { StaffUserFormValues } from '../../components/StaffUserForm';
import useDiscardChangesModal from '../../hooks/useDiscardChangesModal';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import styles from './EditUser.module.css';

interface ConfirmSoftDeleteModalProps extends ModalProps {
  userFullName: string;
  onSuccess?: () => void | Promise<unknown>;
}

const ConfirmSoftDeleteModal = ({ userFullName, ...props }: ConfirmSoftDeleteModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      okButtonProps={{
        htmlType: 'button',
      }}
      okText={t('Sim, deletar usuário')}
      title={
        <Typography.Text strong>
          <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 20 }} />{' '}
          <Trans>Atenção!</Trans>
        </Typography.Text>
      }
      {...props}
    >
      <Typography.Text>
        <Trans>Ao confirmar, o usuário</Trans>{' '}
        <Typography.Text strong>{userFullName}</Typography.Text>{' '}
        <Trans>
          será excluído permanentemente e não poderá ser recuperado. Tem certeza que deseja
          continuar?
        </Trans>
      </Typography.Text>
    </Modal>
  );
};

const EditUser = () => {
  const { t } = useTranslation();

  const [form] = Form.useForm<StaffUserFormValues>();
  const { tenant, user_id } = useTypedParams({ tenant: String, user_id: Number });
  const queryClient = useQueryClient();

  const [confirmUserSoftDeleteIsOpen, setConfirmUserSoftDeleteIsOpen] = useState<boolean>(false);

  const [searchParams] = useSearchParams();

  const user = useAPIQuery(`/staff-users/${user_id}/`);

  const navigate = useTypedNavigate();

  const [setIsDirty] = useDiscardChangesModal(form);

  useEffect(() => {
    if (user.isFetched && !user.data?.is_active) {
      navigate({ pathname: '/:tenant/settings/cs-staff/staff-users/', params: { tenant } });
    }
  }, [navigate, tenant, user]);

  const mutation = useAPIMutation(
    (variables: StaffUserFormValues, api) => {
      return api.patch(`/staff-users/${user_id}/`, {
        full_name: variables.name,
        email: variables.email,
        username: variables.username,
      });
    },
    {
      onSuccess: () => {
        setIsDirty(false);
        message.success(t('Usuário atualizado!'));
        navigate({ pathname: '/:tenant/settings/cs-staff/staff-users/', params: { tenant } });
      },
      onError: (err, variables, context) => {
        if (err?.response?.data?.email?.error === 'email_already_exists') {
          return notification.error({
            duration: 0,
            message: t('Atenção'),
            description: t('Já existe um usuário com este endereço de E-mail cadastrado.'),
          });
        }

        if (err?.response?.data?.username?.error === 'username_already_exists') {
          return notification.error({
            duration: 0,
            message: t('Atenção'),
            description: t('Já existe uma pessoa com esse nome de Usuário cadastrado.'),
          });
        }

        return queryClient.defaultMutationOptions().onError?.(err, variables, context);
      },
      onSettled: () => {
        queryClient.invalidateQueries('/staff-users/');
        queryClient.invalidateQueries(`/staff-users/${user_id}/`);
      },
    }
  );

  const softDeleteUserMutation = useAPIMutation(
    (_, api) =>
      api.patch(`/staff-users/${user_id}/`, {
        is_active: false,
      }),
    {
      onSuccess: () => {
        message.success(t('Usuário excluído com sucesso!'));
        navigate({ pathname: '/:tenant/settings/cs-staff/staff-users/', params: { tenant } });
      },
      onSettled: () => {
        queryClient.invalidateQueries('/staff-users/');
      },
    }
  );

  return (
    <>
      <ConfirmSoftDeleteModal
        key={`ConfirmSoftDeleteModal-${user_id}`}
        open={confirmUserSoftDeleteIsOpen}
        onOk={() => {
          setConfirmUserSoftDeleteIsOpen(false);
          softDeleteUserMutation.mutate({});
        }}
        onCancel={() => setConfirmUserSoftDeleteIsOpen(false)}
        onSuccess={() => {
          setConfirmUserSoftDeleteIsOpen(false);
        }}
        destroyOnClose
        userFullName={user.data?.full_name || '...'}
      />
      <Drawer
        className={styles.drawer}
        title={
          <PageTitle
            title={user.data?.username || '...'}
            level={2}
            extra={[
              <Button
                key="close"
                type="text"
                onClick={() =>
                  navigate({
                    pathname: '/:tenant/settings/cs-staff/staff-users/',
                    params: { tenant },
                  })
                }
                shape="circle"
              >
                <CloseOutlined />
              </Button>,
            ]}
          />
        }
        maskClosable
        size="large"
        closable={false}
        width={720}
        footer={
          <Row gutter={[16, 16]}>
            <Col>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={mutation.isLoading || softDeleteUserMutation.isLoading}
                onClick={() => form?.submit()}
              >
                {' '}
                <Trans>Salvar</Trans>
              </Button>
            </Col>
            <Col>
              <Button
                loading={mutation.isLoading || softDeleteUserMutation.isLoading}
                onClick={() =>
                  navigate({
                    pathname: '/:tenant/settings/cs-staff/staff-users/',
                    params: { tenant },
                    search: searchParams,
                  })
                }
              >
                <Trans>Fechar</Trans>
              </Button>
            </Col>
            <Col flex={1} />
            <Col>
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={mutation.isLoading || softDeleteUserMutation.isLoading}
                onClick={() => setConfirmUserSoftDeleteIsOpen(true)}
              >
                {' '}
                <Trans>Deletar</Trans>
              </Button>
            </Col>
          </Row>
        }
        open
        onClose={() =>
          navigate({ pathname: '/:tenant/settings/cs-staff/staff-users/', params: { tenant } })
        }
      >
        <Helmet>
          <title>
            {t('Editar | Perfil staff')}
            {user.data?.username || '...'} | {t('Recrutamento e Seleção')}
          </title>
        </Helmet>
        <ErrorResult error={user.error}>
          <Spin spinning={user.isFetching}>
            <StaffUserForm
              form={form}
              initialValues={{
                name: user.data?.full_name || '',
                username: user.data?.username,
                email: user.data?.email,
              }}
              layout="vertical"
              onValuesChange={() => setIsDirty(form.isFieldsTouched())}
              onFinish={(variables) => {
                mutation.mutate(variables);
              }}
            />
          </Spin>
        </ErrorResult>
      </Drawer>
    </>
  );
};

export default withRouteChange()(EditUser);
